'use client';

import { useCallback, useMemo, useState } from 'react';

import { type Accept, type FileRejection, useDropzone } from 'react-dropzone';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

type DropzoneFieldProps = {
    name: string;
    label: string;
    accept?: Accept;
    description?: string;
    setError?: (error: string | null) => void;
};

const inputClassName =
    'border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex h-10 w-full rounded-full border bg-transparent px-4 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50';

const textareaClassName =
    'border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 min-h-40 w-full rounded-2xl border bg-transparent px-4 py-3 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50';

const maxFileSize = 50 * 1024 * 1024;

const DropzoneField = ({ name, label, accept, description, setError }: DropzoneFieldProps) => {
    const [files, setFiles] = useState<File[]>([]);
    const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            setFiles(acceptedFiles);
            setRejectionMessage(null);
            setError?.(null);
        },
        [setError]
    );

    const onDropRejected = useCallback(
        (rejections: FileRejection[]) => {
            if (!rejections.length) {
                setRejectionMessage(null);
                setError?.(null);
                return;
            }
            setRejectionMessage(`Файл(ы) больше 50 МБ`);
            setError?.('Файл(ы) больше 50 МБ');
        },
        [setError]
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        maxSize: maxFileSize,
        onDropRejected,
    });

    const fileNames = useMemo(() => files.map((file) => file.name).join(', '), [files]);

    return (
        <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor={name}>
                {label}
            </label>
            <div
                {...getRootProps({
                    className: cn(
                        'border-border/70 bg-muted/20 flex flex-col items-center justify-center rounded-2xl border border-dashed px-4 py-6 sm:py-10 text-center text-sm',
                        isDragActive && 'border-primary/60 bg-primary/5'
                    ),
                })}
            >
                <input {...getInputProps({ id: name, name, className: 'hidden' })} />
                <div>{isDragActive ? 'Отпустите файлы здесь' : 'Перетащите сюда файлы или'}</div>
                <Button
                    type="button"
                    className="mt-3 inline-flex items-center justify-center rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/80 transition-colors"
                    onClick={open}
                >
                    Выберите файлы
                </Button>
                {fileNames ? <div className="mt-3 text-xs text-muted-foreground">{fileNames}</div> : null}
            </div>
            {description ? <div className="text-xs text-muted-foreground">{description}</div> : null}
            {rejectionMessage ? <div className="text-xs text-red-500">{rejectionMessage}</div> : null}
        </div>
    );
};

type FormAction = (formData: FormData) => void | Promise<void>;

export const AppealForm = ({ action }: { action: FormAction }) => {
    const [errors, setErrors] = useState<Record<string, string | null>>({});
    const setError = useCallback((name: string, error: string | null) => {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
    }, []);

    const hasError = useMemo(() => Object.values(errors).some((error) => error !== null), [errors]);

    return (
        <form action={action} className="flex w-full flex-col gap-5 sm:gap-6" encType="multipart/form-data">
            <div className="flex flex-col gap-3">
                <div className="text-sm font-medium">
                    Ваше имя и фамилия <span className="text-red-500 text-xs">(Обязательно)</span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium" htmlFor="first-name">
                            Имя
                        </label>
                        <Input
                            id="first-name"
                            name="firstName"
                            required
                            autoComplete="given-name"
                            className={inputClassName}
                            placeholder="Имя"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium" htmlFor="last-name">
                            Фамилия
                        </label>
                        <Input
                            id="last-name"
                            name="lastName"
                            required
                            autoComplete="family-name"
                            className={inputClassName}
                            placeholder="Фамилия"
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-medium" htmlFor="email">
                    Email <span className="text-red-500 text-xs">(Обязательно)</span>
                </label>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className={inputClassName}
                    placeholder="Email"
                />
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-medium" htmlFor="phone">
                    Номер телефона <span className="text-red-500 text-xs">(Обязательно)</span>
                </label>
                <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    autoComplete="tel"
                    className={inputClassName}
                    placeholder="+7 (___) ___-__-__"
                />
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-medium" htmlFor="message">
                    Сообщение
                </label>
                <Textarea
                    id="message"
                    name="message"
                    rows={6}
                    className={textareaClassName}
                    placeholder="Опишите ситуацию"
                />
            </div>

            <DropzoneField
                name="photo"
                label="Фото"
                accept={{ 'image/*': [] }}
                setError={(error) => setError('photo', error)}
                description="Макс. размер файла: 50 МБ."
            />
            <DropzoneField
                name="video"
                label="Видео"
                accept={{ 'video/*': [] }}
                setError={(error) => setError('video', error)}
                description="Макс. размер файла: 50 МБ."
            />
            <DropzoneField
                name="file"
                label="Файл"
                setError={(error) => setError('file', error)}
                description="Макс. размер файла: 50 МБ."
            />

            <Button
                type="submit"
                disabled={hasError}
                className="mt-2 inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/80 transition-colors min-h-12"
            >
                Отправить
            </Button>
        </form>
    );
};

